import React, { useState } from 'react';
import { Helmet } from 'react-helmet';
import CustomerNavigation from '../../components/CustomerNavigation';
import KakaoChatButton from '../../components/KakaoChatButton';
import OrderSearchForm from './components/OrderSearchForm';
import OrderProgressIndicator from './components/OrderProgressIndicator';
import OrderDetailsCard from './components/OrderDetailsCard';
import OrderHistorySection from './components/OrderHistorySection';
import EmptyOrderState from './components/EmptyOrderState';
import { supabase } from '../../lib/supabaseClient';

const OrderStatusTracking = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [searchedPhone, setSearchedPhone] = useState('');
  const [currentOrder, setCurrentOrder] = useState(null);
  const [orderHistory, setOrderHistory] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  const normalizePhone = (value) => {
    return (value || '').replace(/\s/g, '').replace(/-/g, '');
  };

  const formatPhoneWithHyphen = (digits) => {
    const d = (digits || '').replace(/\D/g, '');
    if (d.length === 11) return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
    if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
    return '';
  };

  const mapOrderStatusToStep = (status) => {
    switch (status) {
      case '주문 접수':
        return { step: 'received', label: '주문 접수' };
      case '픽업 예정':
        return { step: 'pickup_scheduled', label: '픽업 예정' };
      case '세탁 중':
        return { step: 'in_progress', label: '세탁 진행중' };
      case '배송 예정':
        return { step: 'delivery_scheduled', label: '배송 예정' };
      case '완료':
        return { step: 'completed', label: '완료' };
      default:
        return { step: 'received', label: status || '주문 접수' };
    }
  };

  const toUiOrder = (orderRow) => {
    const mapped = mapOrderStatusToStep(orderRow?.status);
    const items = (orderRow?.order_items || []).map((it) => ({
      brand: it?.brand,
      model: it?.model,
      color: '',
      serviceType: it?.service_type,
      price: Number(it?.price || 0),
    }));

    const statusHistory = [];
    if (orderRow?.order_date) {
      statusHistory.push({ status: 'received', timestamp: orderRow.order_date });
      if (mapped.step !== 'received') {
        statusHistory.push({ status: mapped.step, timestamp: orderRow.order_date });
      }
    }

    return {
      orderNumber: orderRow?.order_number || `#${orderRow?.id}`,
      customerName: orderRow?.customer_name,
      phoneNumber: orderRow?.phone,
      address: orderRow?.address,
      orderDate: orderRow?.order_date,
      estimatedCompletion: null,
      currentStatus: mapped.step,
      statusLabel: mapped.label,
      pickupType: orderRow?.pickup_type,
      pickupInstructions: orderRow?.custom_instructions,
      items,
      totalAmount: Number(orderRow?.total_amount || 0),
      statusHistory,
    };
  };

  const toUiHistoryOrder = (orderRow) => {
    const mapped = mapOrderStatusToStep(orderRow?.status);
    return {
      orderNumber: orderRow?.order_number || `#${orderRow?.id}`,
      orderDate: orderRow?.order_date,
      statusLabel: mapped?.label,
      items: (orderRow?.order_items || []).map((it) => ({
        brand: it?.brand,
        model: it?.model,
        color: '',
        serviceType: it?.service_type,
        price: Number(it?.price || 0),
      })),
      totalAmount: Number(orderRow?.total_amount || 0),
    };
  };

  const handleSearch = async (phoneNumber) => {
    setIsLoading(true);
    setSearchedPhone(phoneNumber);
    setErrorMessage('');

    try {
      const normalized = normalizePhone(phoneNumber);
      const hyphen = formatPhoneWithHyphen(normalized);

      const phoneCandidates = Array.from(new Set(
        [normalized, hyphen]
          .filter((v) => typeof v === 'string' && v.trim().length > 0)
      ));

      let query = supabase
        .from('orders')
        .select('id, order_number, customer_name, phone, address, status, order_date, total_amount, pickup_type, custom_instructions, order_items ( brand, model, service_type, price )')
        .order('order_date', { ascending: false });

      if (phoneCandidates.length > 1) {
        query = query.in('phone', phoneCandidates);
      } else {
        query = query.eq('phone', phoneCandidates[0] || normalized);
      }

      const { data, error } = await query;
      if (error) throw error;

      const rows = data || [];
      if (rows.length === 0) {
        setCurrentOrder(null);
        setOrderHistory([]);
      } else {
        const current = rows[0];
        setCurrentOrder(toUiOrder(current));

        const historyRows = rows.slice(1);
        setOrderHistory(historyRows.map(toUiHistoryOrder));
      }
    } catch (e) {
      setErrorMessage(e?.message || '주문 조회에 실패했습니다.');
      setCurrentOrder(null);
      setOrderHistory([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTrackAnother = () => {
    setCurrentOrder(null);
    setOrderHistory([]);
    setSearchedPhone('');
  };

  return (
    <>
      <Helmet>
        <title>주문 조회 - SneakerCare Pro</title>
        <meta
          name="description"
          content="전화번호로 간편하게 주문 상태를 확인하고 세탁 진행 상황을 실시간으로 추적하세요"
        />
      </Helmet>
      <div className="min-h-screen bg-background">
        <CustomerNavigation />

        <main className="mx-auto max-w-4xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-8 text-center">
            <h1 className="mb-2 text-3xl font-bold text-foreground sm:text-4xl">
              주문 조회
            </h1>
            <p className="text-muted-foreground text-white">
              전화번호로 주문 상태를 확인하세요
            </p>
          </div>

          <div className="space-y-6">
            {!currentOrder && !searchedPhone && (
              <OrderSearchForm onSearch={handleSearch} isLoading={isLoading} />
            )}

            {!isLoading && errorMessage && (
              <div className="rounded-lg bg-error/10 p-4 shadow-card">
                <p className="text-sm font-medium text-error">{errorMessage}</p>
              </div>
            )}

            {isLoading && (
              <div className="flex items-center justify-center rounded-lg bg-card p-12 shadow-card">
                <div className="text-center">
                  <div className="mb-4 inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
                  <p className="text-sm text-muted-foreground">주문 조회 중...</p>
                </div>
              </div>
            )}

            {!isLoading && searchedPhone && !currentOrder && (
              <EmptyOrderState phoneNumber={searchedPhone} />
            )}

            {!isLoading && currentOrder && (
              <>
                <OrderProgressIndicator
                  currentStatus={currentOrder?.currentStatus}
                  statusHistory={currentOrder?.statusHistory}
                />

                <OrderDetailsCard order={currentOrder} />

                {orderHistory?.length > 0 && (
                  <OrderHistorySection orders={orderHistory} />
                )}
              </>
            )}
          </div>
        </main>

        <KakaoChatButton />
      </div>
    </>
  );
};

export default OrderStatusTracking;